import { useCallback, useEffect, useState } from "react";
import { DesignSystemProvider } from "@murm/ant";
import { Auth0Provider } from "@auth0/auth0-react";
import { Provider } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import AppRoutes from "./Routes";
import { HealthPage } from "../pages/HealthPage";
import styles from "./App.module.css";
import Hotjar from "@hotjar/browser";
import { AppShell } from "../components/layouts/AppShell";
import { store } from "../store";
import auth0ClientConfig from "../auth0/clientConfig";
import { useAuth } from "../hooks/useAuth";
import { useSessionExpiryWarning } from "../hooks/useSessionExpiryWarning";
import { Auth0AuthBridge } from "../context/Auth0AuthBridge";
import { GatewayAuthBridge } from "../context/GatewayAuthBridge";
import { normalizeExplorerApiBaseUrl } from "../lib/apiBaseUrl";
import {
  initDatadogRum,
  setDatadogRumUser,
  stopDatadogRumSession,
} from "../lib/datadogRum";
import { trackLogin } from "../lib/analytics";
import { getConfig } from "../lib/runtimeConfig";
import { markSessionExpired } from "../lib/sessionExpiry";

const DEFAULT_GA_ID = "GT-MMKPN7RVN";
const DEFAULT_HOTJAR_SITE_ID = 5215222;
const HOTJAR_VERSION = 6;

/**
 * Must live at module scope (not inside AppContent) so hook order is stable — nested
 * hook factories can trigger "Cannot read properties of null (reading 'useContext')" in production.
 */
function usePageTracking(shouldTrackAnalytics, analyticsConfig) {
  const location = useLocation();

  useEffect(() => {
    let isSubscribed = true;

    const sendPageView = () => {
      if (!window.gtag || !shouldTrackAnalytics || !isSubscribed) return;

      const pageName =
        location.pathname.split("/").filter(Boolean).join("/") || "home";

      const getScreenName = () => {
        const path = location.pathname.split("/").filter(Boolean);
        if (path.length === 0) return "Home Page";

        return path
          .map((segment) =>
            segment
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")
          )
          .join(" - ");
      };

      const queryParams = {};
      const searchParams = new URLSearchParams(location.search);
      for (const [key, value] of searchParams) {
        queryParams[key] = value;
      }

      const trackingData = {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        page_name: pageName,
        page_title: document.title,
        screen_name: getScreenName(),
        page_query_params: queryParams,
      };

      window.gtag("config", analyticsConfig.gaMeasurementId || DEFAULT_GA_ID, {
        ...trackingData,
        send_page_view: true,
      });

      window.gtag("event", "page_view", trackingData);
    };

    sendPageView();

    return () => {
      isSubscribed = false;
    };
  }, [
    location.pathname,
    location.search,
    shouldTrackAnalytics,
    analyticsConfig.gaMeasurementId,
  ]);
}

function PageTracker({ shouldTrackAnalytics, analyticsConfig }) {
  usePageTracking(shouldTrackAnalytics, analyticsConfig);
  return null;
}

function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    logout,
    expiresAt,
  } = useAuth();
  if (typeof window !== "undefined") {
    window.__getAccessTokenSilently = (options = {}) =>
      getAccessTokenSilently({
        ...options,
        authorizationParams: {
          ...(auth0ClientConfig.audience
            ? { audience: auth0ClientConfig.audience }
            : {}),
          ...options.authorizationParams,
        },
      });
    window.__getAuthUser = async () => user;
    window.__isAuthenticated = isAuthenticated;
    window.__auth0IsLoading = isLoading;
  }

  const recoverFromInvalidSession = useCallback(async () => {
    // returnTo must be the bare origin (Auth0's only allowed logout URL), so the expired signal
    // travels via sessionStorage -- see lib/sessionExpiry.js.
    markSessionExpired();
    await logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }, [logout]);

  useEffect(() => {
    window.__recoverFromInvalidSession = recoverFromInvalidSession;
    return () => {
      delete window.__recoverFromInvalidSession;
    };
  }, [recoverFromInvalidSession]);

  useSessionExpiryWarning(isAuthenticated ? expiresAt : undefined, recoverFromInvalidSession);

  const [shouldTrackAnalytics, setShouldTrackAnalytics] = useState(true);
  const [analyticsConfig, setAnalyticsConfig] = useState({
    gaMeasurementId: DEFAULT_GA_ID,
    hotjarSiteId: DEFAULT_HOTJAR_SITE_ID,
    pendoAppId: "",
    ddRumApplicationId: "",
    ddRumClientToken: "",
    ddRumSite: "",
    ddRumService: "",
    ddRumEnv: "",
    ddRumVersion: "",
    ddRumSessionSampleRate: "",
    ddRumSessionReplaySampleRate: "",
  });

  useEffect(() => {
    const baseUrl = normalizeExplorerApiBaseUrl(
      getConfig("BASE_API_URL")
    );
    const configUrl = baseUrl
      ? `${baseUrl}/api/config`
      : "/api/config";
    const ac = new AbortController();
    const tid = setTimeout(() => ac.abort(), 15000);
    fetch(configUrl, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        setAnalyticsConfig({
          gaMeasurementId: data.gaMeasurementId || DEFAULT_GA_ID,
          hotjarSiteId: data.hotjarSiteId ? Number(data.hotjarSiteId) : DEFAULT_HOTJAR_SITE_ID,
          pendoAppId: data.pendoAppId || "",
          ddRumApplicationId: data.ddRumApplicationId || "",
          ddRumClientToken: data.ddRumClientToken || "",
          ddRumSite: data.ddRumSite || "",
          ddRumService: data.ddRumService || "",
          ddRumEnv: data.ddRumEnv || "",
          ddRumVersion: data.ddRumVersion || "",
          ddRumSessionSampleRate: data.ddRumSessionSampleRate ?? "",
          ddRumSessionReplaySampleRate: data.ddRumSessionReplaySampleRate ?? "",
        });
      })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("[Explorer] GET /api/config failed — RUM/GA keys unavailable", err);
        }
      })
      .finally(() => clearTimeout(tid));

    return () => {
      clearTimeout(tid);
      ac.abort();
    };
  }, []);

  useEffect(() => {
    const { hotjarSiteId, pendoAppId } = analyticsConfig;

    // GTM (GTM-MKPN7RVN) in index.html owns GA4 script loading.
    // Ensure dataLayer + gtag stubs exist as a safety net so the calls below
    // queue correctly until GTM fires the GA4 tag (GT-MMKPN7RVN).
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };

    // Empty API site id falls back to the production default: dev sessions must not reach it.
    if (hotjarSiteId && process.env.NODE_ENV !== "development") {
      Hotjar.init(hotjarSiteId, HOTJAR_VERSION);
    }

    if (isLoading || !isAuthenticated) {
      setShouldTrackAnalytics(false);
      return;
    }

    Promise.resolve(user)
      .then((user) => {
        setShouldTrackAnalytics(true);
        window.gtag("consent", "update", { analytics_storage: "granted" });
        trackLogin(user);

        if (pendoAppId) {
          const visitor = {
            id: user?.sub,
            email: user?.email,
            full_name: user?.name,
          };
          const account = { id: "explorer" };

          if (typeof window.pendo === "undefined") {
            // First load: inject agent and initialize.
            const script = document.createElement("script");
            script.src = `https://cdn.pendo.io/agent/static/${pendoAppId}/pendo.js`;
            script.async = true;
            script.onload = () => {
              if (window.pendo && visitor.id) {
                window.pendo.initialize({ visitor, account });
              }
            };
            document.head.appendChild(script);
          } else if (window.pendo?.isReady?.() && visitor.id) {
            // Agent already loaded — re-identify on user change.
            window.pendo.identify({ visitor, account });
          }
        }
      })
      .catch(() => {
        setShouldTrackAnalytics(false);
      });
  }, [analyticsConfig, user, isAuthenticated, isLoading]);

  // RUM: start when /api/config has keys (including anonymous sessions).
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const appId = (analyticsConfig.ddRumApplicationId || "").trim();
    const token = (analyticsConfig.ddRumClientToken || "").trim();
    if (!appId || !token) {
      return;
    }
    initDatadogRum(
      analyticsConfig,
      isAuthenticated ? user : null
    );
    setDatadogRumUser(isAuthenticated ? user : null);
  }, [analyticsConfig, user, isAuthenticated, isLoading]);

  return (
    <Provider store={store}>
      <>
        <PageTracker
          shouldTrackAnalytics={shouldTrackAnalytics}
          analyticsConfig={analyticsConfig}
        />
        <AppShell>
          <AppRoutes />
        </AppShell>
      </>
    </Provider>
  );
}

/** Shown for non-/health routes when CRA Auth0 env vars are missing (e.g. CI without secrets). */
function Auth0NotConfigured() {
  const isDev = process.env.NODE_ENV === "development";
  return (
    <div className={styles.notConfigured}>
      <h1 className={styles.notConfiguredTitle}>Auth0 is not configured</h1>
      <p>
        This build is missing Auth0 domain/client for the SPA (typically{" "}
        <code>AUTH0_ISSUER</code> + <code>AUTH0_CLIENT_ID</code> in repo root <code>.env</code>, or{" "}
        <code>REACT_APP_AUTH0_*</code>). CRA only sees <code>REACT_APP_*</code>;{" "}
        <code>make build</code> / <code>make publish-frontend</code> derives domain from issuer and
        mirrors client/audience when <code>REACT_APP_*</code> are unset.
      </p>
      {isDev ? (
        <p>
          <strong>Local dev:</strong> the developer loop (<code>make -C apps/explorer dev</code>) needs no
          Auth0: it signs you in through the local gateway. For a build against a real tenant, set{" "}
          <code>AUTH0_ISSUER</code>, <code>AUTH0_CLIENT_ID</code> and <code>AUTH0_AUDIENCE</code> in the
          repository root <code>.env</code> (see <code>/.env.example</code>). To provision Auth0 with
          Terraform, see{" "}
          <code>ops/integrations/auth0/README.md</code> and run{" "}
          <code>terraform output -raw env_for_explorer_root</code> after <code>terraform apply</code>.
        </p>
      ) : (
        <p>
          Copy <code>web/.env.production.example</code> to <code>.env.production</code> or set vars in
          the repository root <code>.env</code>, then run <code>make publish-frontend</code> (or{" "}
          <code>npm run build</code>). Alternatively export <code>REACT_APP_AUTH0_*</code> in your
          shell before <code>make publish-frontend</code>.
        </p>
      )}
      <p>
        <code>REACT_APP_AUTH0_DOMAIN</code> must be the host only (e.g.{" "}
        <code>tenant.us.auth0.com</code>), not <code>https://</code>.
      </p>
      <p>
        In the Auth0 dashboard, add your site origin (e.g.{" "}
        <code>https://dxxxx.cloudfront.net</code> or <code>http://localhost:3000</code>) to{" "}
        <strong>Allowed Callback URLs</strong>, <strong>Allowed Logout URLs</strong>, and{" "}
        <strong>Allowed Web Origins</strong> (or define them via{" "}
        <code>ops/integrations/auth0/terraform</code>).
      </p>
    </div>
  );
}

function App() {
  // Border-gateway migration: a plain conditional RENDER, decided once per page load from
  // static runtime config -- not a conditional hook call. See context/AuthContext.js's header
  // comment for why the branch has to live here rather than inside useAuth() itself.
  const authMode = getConfig("AUTH_MODE") || "auth0-spa";

  if (authMode === "gateway") {
    return (
      <DesignSystemProvider>
        <Router>
          <GatewayAuthBridge>
            <AppContent />
          </GatewayAuthBridge>
        </Router>
      </DesignSystemProvider>
    );
  }

  const domainOk = (auth0ClientConfig.domain || "").trim();
  const clientOk = (auth0ClientConfig.clientId || "").trim();
  const authConfigured = Boolean(domainOk && clientOk);

  return (
    <DesignSystemProvider>
      <Router>
        {authConfigured ? (
          <Auth0Provider
            domain={domainOk}
            clientId={clientOk}
            authorizationParams={{
              redirect_uri: window.location.origin,
              audience: auth0ClientConfig.audience || undefined,
              scope: "openid profile email offline_access",
            }}
            cacheLocation="localstorage"
            useRefreshTokens
          >
            <Auth0AuthBridge>
              <AppContent />
            </Auth0AuthBridge>
          </Auth0Provider>
        ) : (
          <Routes>
            <Route path="/health" element={<HealthPage />} />
            <Route path="*" element={<Auth0NotConfigured />} />
          </Routes>
        )}
      </Router>
    </DesignSystemProvider>
  );
}

export default App;
